import React, { useEffect, useRef, useState } from "react"

import { trpc } from "../lib/trpc"

interface User {
  id: string
  name: string
  email: string
  avatarUrl?: string | null
}

interface AssigneeInfo {
  id: string | null
  name: string | null
  email: string | null
  avatarUrl: string | null
}

interface AssigneeSelectorProps {
  currentAssignee: AssigneeInfo | null
  taskId: string
  onAssigneeChange: (taskId: string, assigneeId: string | null) => void
}

const AssigneeSelector: React.FC<AssigneeSelectorProps> = ({ currentAssignee, taskId, onAssigneeChange }) => {
  const [showSelector, setShowSelector] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const selectorRef = useRef<HTMLDivElement>(null)

  // Get all available users
  const { data: usersData, isLoading } = trpc.userManagement.getAllUsers.useQuery({
    page: 1,
    limit: 100,
    isActive: true,
  })

  // Update task assignee mutation
  const updateAssigneeMutation = trpc.brain.updateTaskAssignee.useMutation({
    onSuccess: () => {
      setShowSelector(false)
    },
  })

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowSelector(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Filter users based on search term
  const filteredUsers =
    usersData?.users?.filter(
      (user) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || []

  const handleAssigneeSelect = (userId: string | null) => {
    if (userId !== currentAssignee?.id) {
      onAssigneeChange(taskId, userId)
      updateAssigneeMutation.mutate({
        taskId,
        assigneeId: userId,
      })
    }
    setShowSelector(false)
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  const renderAssigneeDisplay = () => {
    if (!currentAssignee?.id) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation()
            setShowSelector(!showSelector)
          }}
          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full cursor-pointer  transition-colors border border-gray-300"
          title="Assign task to someone"
        >
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Assign
        </button>
      )
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation()
          setShowSelector(!showSelector)
        }}
        className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full cursor-pointer hover:bg-blue-100 transition-colors bg-blue-50 border border-blue-200 text-blue-800"
        title={`Assigned to ${currentAssignee.name} (${currentAssignee.email})`}
      >
        {currentAssignee.avatarUrl ? (
          <img src={currentAssignee.avatarUrl} alt={currentAssignee.name || ""} className="w-4 h-4 rounded-full mr-1" />
        ) : (
          <div className="w-4 h-4 rounded-full mr-1 bg-blue-500 text-white text-xs flex items-center justify-center">
            {getInitials(currentAssignee.name || "")}
          </div>
        )}
        {currentAssignee.name}
        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    )
  }

  return (
    <div className="relative">
      {renderAssigneeDisplay()}

      {/* Assignee selector popover */}
      {showSelector && (
        <div
          ref={selectorRef}
          className="absolute top-full left-0 mt-1 bg-background border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-80 max-h-96 overflow-hidden"
        >
          <div className="space-y-3">
            <div className="text-sm font-medium text-primary">Assign task to:</div>

            {/* Search input */}
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />

            {/* User list */}
            <div className="max-h-64 overflow-y-auto space-y-1">
              {/* Unassign option */}
              <button
                onClick={() => handleAssigneeSelect(null)}
                disabled={updateAssigneeMutation.isLoading}
                className={`w-full text-left p-2 rounded-lg transition-colors  disabled:opacity-50 ${
                  !currentAssignee?.id ? "bg-blue-50 border border-blue-200" : "border border-transparent"
                }`}
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full mr-3 bg-foregroundx flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium ">
                      Unassigned
                      {!currentAssignee?.id && <span className="ml-2 text-xs ">(current)</span>}
                    </div>
                    <div className="text-xs text-gray-500">Remove assignee from this task</div>
                  </div>
                </div>
              </button>

              {isLoading ? (
                <div className="text-sm text-gray-500 text-center py-4">Loading users...</div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const isCurrentAssignee = user.id === currentAssignee?.id
                  return (
                    <button
                      key={user.id}
                      onClick={() => handleAssigneeSelect(user.id)}
                      disabled={updateAssigneeMutation.isLoading}
                      className={`w-full text-left p-2 rounded-lg transition-colors  disabled:opacity-50 ${
                        isCurrentAssignee ? "bg-blue-50 border border-blue-200" : "border border-transparent"
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full mr-3 overflow-hidden">
                          {user.avatarUrl ? (
                            <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-blue-500 text-white text-sm flex items-center justify-center">
                              {getInitials(user.name)}
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium  truncate">
                            {user.name}
                            {isCurrentAssignee && <span className="ml-2 text-xs ">(current)</span>}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                        </div>
                      </div>
                    </button>
                  )
                })
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">
                  {searchTerm ? "No users found matching your search" : "No users available"}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-border">
              <button
                onClick={() => setShowSelector(false)}
                className="px-3 py-1.5 text-sm  hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              {updateAssigneeMutation.isLoading && (
                <div className="flex items-center gap-2 text-sm ">
                  <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AssigneeSelector
